import React, { useState, useEffect, useRef } from 'react';
import {
  Building2,
  Store,
  Mail,
  Phone,
  MapPin,
  FileText,
  Calendar,
  Trash2,
  CreditCard,
  Image as ImageIcon,
  Check,
  X,
  Download,
  Eye,
  AlertCircle,
  Loader,
  User,
  ClipboardCheck,
  Edit,
  Save
} from 'lucide-react';
import Select from 'react-select';
import { FileText as FileTextIcon } from "lucide-react";
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { useAppSelector } from '../../store/hooks/reduxHooks';
import { getImageUrl, hasDocument, MerchantDoc, isPdfUrl } from './MerchantKycUtils';
import { fetchMerchantDocument, updateMerchant, updateMerchantByMerchantId, handleDocumentUpload, getPartnersList } from '../../services/merchant';
import { showToast } from '../../utils/toast';
import { UserRole } from '../../types/auth';


// Type for document type
type DocumentType = 'shopFront' | 'shopInner' | 'evCer';

// Merchant interface
interface Merchant {
  _id: string;
  mobileNo: string;
  merchantId: string;
  afpId: string;
  isEv: boolean;
  partnerisEv: boolean;
  img: string;
  kycShop: {
    name: string;
    address: string;
    state: string;
    city: string;
    pin: string;
    note: string;
  };
  kycBank: {
    accountNo: string;
    ifsc: string;
    holderName: string;
  };
  kycDocs: {
    shopFront: string;
    shopInner: string;
    evCer: string;
  };
  suspended: boolean;
  activated: boolean;
  kyc: {
    status: boolean;
    bank: boolean;
    shop: boolean;
    docs: boolean;
  };
  verified: boolean;
  eVerified: boolean;
  activatedBy: string | null;
  lastLogin: string;
  createdAt: string;
  updatedAt: string;
  email: string;
  kycGstNo: string;
  name: string
  logs?: string[];
}

// Document interface
interface Document {
  type: DocumentType;
  label: string;
  url?: string;
}

interface MerchantDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  merchant: Merchant;
  canManage: boolean;
  onDelete: () => void;
  onDocumentDelete: (documentType: string) => void;
  onUpdate: () => void;
}

const MerchantDetailModal: React.FC<MerchantDetailModalProps> = ({
  isOpen,
  onClose,
  merchant,
  canManage,
  onDelete,
  onDocumentDelete,
  onUpdate
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [docPreviews, setDocPreviews] = useState<{ [key in DocumentType]?: string }>({});
  const [documentUploaded, setDocumentUploaded] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);



  // Store the original mobile number for API calls
  const originalMobileNoRef = useRef<string>('');

  // Form data for editing
  const [formData, setFormData] = useState<Partial<Merchant>>({
    name: '',
    email: '',
    mobileNo: '',
    afpId: '',
    kycGstNo: '',
    kycShop: {
      name: '',
      address: '',
      state: '',
      city: '',
      pin: '',
      note: ''
    },
    kycBank: {
      accountNo: '',
      ifsc: '',
      holderName: ''
    }
  });

  // Get auth token
  const { token } = useAppSelector(state => state.auth);

  // Initialize form data when merchant changes
  useEffect(() => {
    if (merchant) {
      // Store original mobile for API calls
      originalMobileNoRef.current = merchant.mobileNo || '';

      setFormData({
        name: merchant.name || '',
        email: merchant.email || '',
        mobileNo: merchant.mobileNo || '',
        afpId: merchant.afpId || '',
        kycGstNo: merchant.kycGstNo || '',
        kycShop: {
          name: merchant.kycShop?.name || '',
          address: merchant.kycShop?.address || '',
          state: merchant.kycShop?.state || '',
          city: merchant.kycShop?.city || '',
          pin: merchant.kycShop?.pin || '',
          note: merchant.kycShop?.note || ''
        },
        kycBank: {
          accountNo: merchant.kycBank?.accountNo || '',
          ifsc: merchant.kycBank?.ifsc || '',
          holderName: merchant.kycBank?.holderName || ''
        }
      });
    }
  }, [merchant]);

  // Handle input change for top-level fields
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  // Handle input change for nested shop fields
  const handleShopInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      kycShop: {
        ...prev.kycShop!,
        [name]: value
      }
    }));
  };

  // Handle input change for nested bank fields
  const handleBankInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value = '' } = e.target;
    setFormData(prev => ({
      ...prev,
      kycBank: {
        ...prev.kycBank!,
        [name]: value
      }
    }));
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!formData.mobileNo) {
      showToast.error('Mobile number is required');
      return;
    }

    setIsSubmitting(true);

    try {
      // Only send fields that have changed
      const changedData: Partial<Merchant> = {};

      // Check top-level fields
      if (formData.name !== merchant.name) changedData.name = formData.name;
      if (formData.email !== merchant.email) changedData.email = formData.email;
      if (formData.mobileNo !== merchant.mobileNo) changedData.mobileNo = formData.mobileNo;
      if (formData.afpId !== merchant.afpId) changedData.afpId = formData.afpId;
      if (formData.kycGstNo !== merchant.kycGstNo) changedData.kycGstNo = formData.kycGstNo;

      // Check shop fields
      const shopChanged =
        formData.kycShop?.name !== merchant.kycShop?.name ||
        formData.kycShop?.address !== merchant.kycShop?.address ||
        formData.kycShop?.state !== merchant.kycShop?.state ||
        formData.kycShop?.city !== merchant.kycShop?.city ||
        formData.kycShop?.pin !== merchant.kycShop?.pin ||
        formData.kycShop?.note !== merchant.kycShop?.note;

      if (shopChanged) {
        changedData.kycShop = formData.kycShop;
      }

      // Check bank fields
      const bankChanged =
        formData.kycBank?.accountNo !== merchant.kycBank?.accountNo ||
        formData.kycBank?.ifsc !== merchant.kycBank?.ifsc ||
        formData.kycBank?.holderName !== merchant.kycBank?.holderName;

      if (bankChanged) {
        changedData.kycBank = formData.kycBank;
      }

      // If nothing has changed, exit edit mode
      if (Object.keys(changedData).length === 0) {
        console.log(documentUploaded)
        if (documentUploaded) {
          // Document was uploaded, just exit edit mode silently
          setIsEditing(false);
          setDocumentUploaded(false); // Reset for next edit session
          return;
        }
        showToast.info('No changes were made');
        setIsEditing(false);
        return;
      }

      console.log('[MERCHANT-EDIT] Updating merchant with changes:', changedData);

      // Check if mobile number is changing
      const isMobileNoChanging = formData.mobileNo !== merchant.mobileNo;

      let success = false;

      if (isMobileNoChanging && merchant.merchantId) {
        // If mobile number is changing, use merchantId for the API call
        console.log('[MERCHANT-EDIT] Mobile number is changing, using merchantId for update:', merchant.merchantId);
        success = await updateMerchantByMerchantId(merchant.merchantId, changedData, token);
      } else {
        // Use the mobile number for the API call if not changing
        console.log('[MERCHANT-EDIT] Using mobile number for update:', merchant.mobileNo);
        success = await updateMerchant(merchant.mobileNo, changedData, token);
      }

      if (success) {
        setIsEditing(false);
        setDocumentUploaded(false); // Reset the flag
        onUpdate(); // Refresh merchant data
      } else {
        // Error toast is already shown by the service function if needed
      }
    } catch (error: any) {
      console.error('[MERCHANT-EDIT] Error updating merchant:', error);
      showToast.error(error.message || 'An error occurred while updating merchant');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    // Reset form data to original merchant data
    setFormData({
      name: merchant.name || '',
      email: merchant.email || '',
      mobileNo: merchant.mobileNo || '',
      afpId: merchant.afpId || '',
      kycGstNo: merchant.kycGstNo || '',
      kycShop: {
        name: merchant.kycShop?.name || '',
        address: merchant.kycShop?.address || '',
        state: merchant.kycShop?.state || '',
        city: merchant.kycShop?.city || '',
        pin: merchant.kycShop?.pin || '',
        note: merchant.kycShop?.note || ''
      },
      kycBank: {
        accountNo: merchant.kycBank?.accountNo || '',
        ifsc: merchant.kycBank?.ifsc || '',
        holderName: merchant.kycBank?.holderName || ''
      }
    });
    setIsEditing(false);
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get document list
  const getDocuments = (): Document[] => {
    return [
      {
        type: 'shopFront',
        label: 'Shop Front Photo'
      },
      {
        type: 'shopInner',
        label: 'Shop Inner Photo'
      },
      {
        type: 'evCer',
        label: 'EV Certificate'
      }
    ];
  };

  // Handle preview image
  const handlePreviewImage = (url: string) => {
    // Check if it's a PDF URL
    if (url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('application/pdf')) {
      // For PDFs, open in a new window to let the browser handle it
      window.open(url, '_blank');
      return;
    }

    // For images, show in our preview dialog
    setPreviewImage(url);
    setPreviewOpen(true);
  };

  // Handle download image
  const handleDownloadImage = (url: string, documentName: string) => {
    // Check if URL is a PDF
    const isPdf = url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('application/pdf');
    const fileExtension = isPdf ? 'pdf' : 'jpg';

    console.log(`[DEBUG] Downloading document: ${documentName}, URL: ${url}, isPDF: ${isPdf}`);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${merchant.name || merchant.merchantId}-${documentName}.${fileExtension}`;

    // For PDFs in some browsers, we may need to use a different approach
    if (isPdf && navigator.userAgent.toLowerCase().includes('safari')) {
      // Opening in a new window for Safari which might handle PDFs differently
      window.open(url, '_blank');
    } else {
      // Standard download approach for most cases
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Apply CSS class conditionally
  const cn = (...classes: (string | boolean | undefined)[]) => {
    return classes.filter(Boolean).join(' ');
  };
  const { user } = useAppSelector(state => state.auth);

  // Refs for file inputs
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    // When modal opens, fetch the latest document URLs for all doc types
    const fetchLatestPreviews = async () => {
      if (isOpen && merchant && token) {
        const docTypes: DocumentType[] = ['shopFront', 'shopInner', 'evCer'];
        const previews: { [key in DocumentType]?: string } = {};
        for (const docType of docTypes) {
          if (merchant.kycDocs && merchant.kycDocs[docType]) {
            const url = await fetchMerchantDocument(merchant.mobileNo, docType, token, true);
            if (url) {
              previews[docType] = url; // Use the signed URL as-is!
            }
          }
        }
        setDocPreviews(previews);
      }
      // Optionally clear previews on close
      if (!isOpen) {
        setDocPreviews({});
      }
    };
    fetchLatestPreviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, merchant?._id, token]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const partners = await getPartnersList();

        // If role is cleverpeuser, filter by user.groups
        if (
          user?.role === UserRole.CLEVERPE_USER &&
          Array.isArray(user?.groups)
        ) {
          const filteredPartners = partners.filter(p =>
            user.groups!.includes(p)
          );
          setAvailableGroups(filteredPartners);
        } else {
          setAvailableGroups(partners);
        }
      } catch (error) {
        console.error('Error fetching partner groups:', error);
      }
    };
    fetchGroups();
  }, [user]);

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={() => {

          onClose();

        }}
        title={`Merchant Details: ${merchant.name || merchant.merchantId}`}
        size="lg"
      >
        <div className="py-2 no-scrollbar">
          {/* Merchant Header */}
          <div className="flex items-center mb-6">
            <div className="flex items-center justify-center w-12 h-12 mr-3 text-xl font-bold text-white rounded-full bg-gradient-to-r from-blue-500 to-indigo-600">
              {merchant.name ? merchant.name.charAt(0) : merchant.merchantId.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">{merchant.name || merchant.merchantId}</h2>
              <div className="flex items-center mt-1 text-sm">
                <span className="mr-2 text-gray-500">ID: {merchant.merchantId}</span>
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-xs font-medium",
                  merchant.suspended
                    ? "bg-red-100 text-red-700"
                    : "bg-green-100 text-green-700"
                )}>
                  {merchant.suspended ? 'Suspended' : 'Active'}
                </span>
                <span className={cn(
                  "ml-2 px-2 py-0.5 rounded-full text-xs font-medium",
                  merchant.kyc?.status
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                )}>
                  {merchant.kyc?.status ? 'KYC Verified' : 'KYC Pending'}
                </span>
              </div>
            </div>

            {canManage && (
              <div className="flex items-center ml-auto space-x-2">
                {!isEditing ? (
                  <>
                    {user?.role !== UserRole.CLEVERPE_USER && (
                      <Button
                        variant={merchant.suspended ? "secondary" : "danger"}
                        size="sm"
                        onClick={onDelete}
                        className={
                          merchant.suspended ? "bg-green-600 text-white hover:bg-green-700" : ""
                        }
                      >
                        {merchant.suspended ? (
                          <>
                            <Check size={14} className="mr-1" />
                            Reactivate
                          </>
                        ) : (
                          <>
                            <Trash2 size={14} className="mr-1" />
                            Terminate
                          </>
                        )}
                      </Button>
                    )}


                    {user?.role !== UserRole.CLEVERPE_USER && (
                      <>
                        <Button
                          variant={merchant.kyc?.status ? "outline" : "primary"}
                          size="sm"
                          onClick={async () => {
                            try {
                              setIsSubmitting(true);
                              const updatedData = {
                                kyc: {
                                  ...merchant.kyc,
                                  status: !merchant.kyc.status
                                }
                              };

                              let success = false;

                              if (merchant.merchantId) {
                                success = await updateMerchantByMerchantId(merchant.merchantId, updatedData, token);
                              } else {
                                success = await updateMerchant(merchant.mobileNo, updatedData, token);
                              }

                              if (success) {
                                const updatedMerchant = {
                                  ...merchant,
                                  kyc: {
                                    ...merchant.kyc,
                                    status: !merchant.kyc.status
                                  }
                                };

                                // @ts-ignore – manually override local state
                                // merchant.kyc.status = !merchant.kyc.status;

                                onUpdate();
                                showToast.success(`KYC status ${!merchant.kyc.status ? 'verified' : 'set to pending'}`);
                              }
                            } catch (error: any) {
                              console.error('[KYC-TOGGLE] Error updating KYC status:', error);
                              showToast.error(error.message || 'An error occurred while updating KYC status');
                            } finally {
                              setIsSubmitting(false);
                            }
                          }}
                          disabled={isSubmitting}
                          className={merchant.kyc?.status ? "bg-yellow-500 text-white hover:bg-yellow-600 border-yellow-500" : "bg-green-600 text-white hover:bg-green-700"}
                        >
                          {isSubmitting ? (
                            <span className="flex items-center">
                              <Loader size={10} className="mr-1 animate-spin" />
                              Updating...
                            </span>
                          ) : (
                            <span>
                              {merchant.kyc?.status ? 'Set KYC Pending' : 'Set KYC Verified'}
                            </span>
                          )}
                        </Button>

                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setIsEditing(true)}
                        >
                          <Edit size={14} className="mr-1" />
                          Edit
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveChanges}
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      <Save size={14} className="mr-1" />
                      Save Changes
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="details">Merchant Details</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="bank">Bank Details</TabsTrigger>
              <TabsTrigger value="logs">Logs</TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Personal Details */}
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                  <h3 className="mb-3 text-sm font-medium text-gray-700">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <Mail size={16} className="text-gray-500 mt-0.5 mr-2" />
                      <div className="w-full">
                        {isEditing ? (
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              Email
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="text-sm font-medium text-gray-800">{merchant.email}</div>
                            <div className="text-xs text-gray-500">Email</div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Phone size={16} className="text-gray-500 mt-0.5 mr-2" />
                      <div className="w-full">
                        {isEditing ? (
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              Mobile Number
                            </label>
                            <input
                              type="text"
                              name="mobileNo"
                              value={formData.mobileNo}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="text-sm font-medium text-gray-800">{merchant.mobileNo}</div>
                            <div className="text-xs text-gray-500">Mobile Number</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Details */}
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                  <h3 className="mb-3 text-sm font-medium text-gray-700">Business Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <Building2 size={16} className="text-gray-500 mt-0.5 mr-2" />
                      <div className="w-full">
                        {isEditing ? (
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              GROUP ID
                            </label>

                            <Select
                              value={
                                formData.afpId
                                  ? { value: formData.afpId, label: formData.afpId }
                                  : null
                              }
                              onChange={(option) =>
                                setFormData(prev => ({
                                  ...prev,
                                  afpId: option ? option.value : ""
                                }))
                              }
                              options={availableGroups.map(group => ({
                                value: group,
                                label: group
                              }))}
                              isClearable={false}
                              placeholder="Search or select group..."
                              className="react-select-container"
                              classNamePrefix="react-select"
                              styles={{
                                control: (baseStyles) => ({
                                  ...baseStyles,
                                  borderColor: '#D1D5DB',
                                  borderRadius: '0.5rem',
                                  minHeight: '42px',
                                  boxShadow: 'none'
                                }),
                                placeholder: (baseStyles) => ({
                                  ...baseStyles,
                                  color: '#9CA3AF'
                                }),
                                option: (baseStyles, state) => ({
                                  ...baseStyles,
                                  backgroundColor: state.isSelected
                                    ? '#E0E7FF'
                                    : state.isFocused
                                      ? '#F3F4F6'
                                      : 'transparent',
                                  color: state.isSelected ? '#4F46E5' : '#374151',
                                  ':active': {
                                    backgroundColor: '#E0E7FF'
                                  }
                                })
                              }}
                            />
                          </div>
                        ) : (
                          <>
                            <div className="text-sm font-medium text-gray-800">{merchant.afpId}</div>
                            <div className="text-xs text-gray-500">GROUP ID</div>
                          </>
                        )}


                      </div>
                    </div>
                    <div className="flex items-start">
                      <Store size={16} className="text-gray-500 mt-0.5 mr-2" />
                      <div className="w-full">
                        {isEditing ? (
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              Business Name
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="text-sm font-medium text-gray-800">{merchant.name || merchant.merchantId}</div>
                            <div className="text-xs text-gray-500">Business Name</div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <FileText size={16} className="text-gray-500 mt-0.5 mr-2" />
                      <div className="w-full">
                        {isEditing ? (
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              GSTIN Number
                            </label>
                            <input
                              type="text"
                              name="kycGstNo"
                              value={formData.kycGstNo}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="text-sm font-medium text-gray-800">{merchant.kycGstNo || 'Not Provided'}</div>
                            <div className="text-xs text-gray-500">GSTIN Number</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Details */}
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                  <h3 className="mb-3 text-sm font-medium text-gray-700">Shop Address</h3>
                  {isEditing ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          Shop Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.kycShop?.name}
                          onChange={handleShopInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          Address
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={formData.kycShop?.address}
                          onChange={handleShopInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">
                            City
                          </label>
                          <input
                            type="text"
                            name="city"
                            value={formData.kycShop?.city}
                            onChange={handleShopInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">
                            State
                          </label>
                          <input
                            type="text"
                            name="state"
                            value={formData.kycShop?.state}
                            onChange={handleShopInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          PIN Code
                        </label>
                        <input
                          type="text"
                          name="pin"
                          value={formData.kycShop?.pin}
                          onChange={handleShopInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          Notes
                        </label>
                        <textarea
                          name="note"
                          value={formData.kycShop?.note}
                          onChange={handleShopInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={2}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start">
                      <MapPin size={16} className="text-gray-500 mt-0.5 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-800">{merchant.kycShop.address}</div>
                        <div className="text-xs text-gray-500">
                          {merchant.kycShop.city}, {merchant.kycShop.state} - {merchant.kycShop.pin}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Account Details */}
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                  <h3 className="mb-4 text-sm font-semibold text-gray-700">
                    Account Information
                  </h3>

                  <div className="space-y-4">
                    {/* Created On */}
                    <div className="flex items-start gap-3">
                      <Calendar size={16} className="text-gray-500 mt-1" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatDate(merchant.createdAt)}
                        </div>
                        <div className="text-xs text-gray-500">Created On</div>
                      </div>
                    </div>

                    {/* Last Login */}
                    <div className="flex items-start gap-3">
                      <Calendar size={16} className="text-gray-500 mt-1" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatDate(merchant.lastLogin)}
                        </div>
                        <div className="text-xs text-gray-500">Last Login On</div>
                      </div>
                    </div>

                    {/* KYC Status */}
                    <div className="flex items-start gap-3">
                      {merchant.kyc?.status ? (
                        <Check size={16} className="text-green-500 mt-1" />
                      ) : (
                        <X size={16} className="text-red-500 mt-1" />
                      )}

                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {merchant.kyc?.status ? "Verified" : "Not Verified"}
                        </div>
                        <div className="text-xs text-gray-500">KYC Status</div>
                      </div>
                    </div>
                  </div>
                </div>


              </div>
            </TabsContent>

            <TabsContent value="documents" className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {getDocuments()
                  .filter((doc) => doc.type !== 'evCer' || merchant.partnerisEv)
                  .map((doc) => {

                    const url = getImageUrl(merchant, doc.type);
                    const dataAttr = `${merchant.mobileNo}-${doc.type}`;

                    return (
                      <div
                        key={doc.type}
                        className={cn(
                          "p-4 rounded-lg border border-gray-200",
                          hasDocument(merchant, doc.type) ? "bg-white" : "bg-gray-50"
                        )}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="flex items-center text-sm font-medium text-gray-700">
                            <ImageIcon size={14} className="mr-1.5" />
                            {doc.label}
                          </h3>
                          {hasDocument(merchant, doc.type) && canManage && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-auto p-1 text-red-500 hover:text-red-700"
                              onClick={() => onDocumentDelete(doc.type)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>

                        {hasDocument(merchant, doc.type) || docPreviews[doc.type] ? (
                          <div className="space-y-2">
                            <div className="relative w-full h-32 overflow-hidden bg-gray-100 rounded">
                              <img
                                src={docPreviews[doc.type] || url}
                                alt={doc.label}
                                className="object-cover w-full h-full"
                                data-merchant-doc={dataAttr}
                                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = getImageUrl(null, doc.type);
                                }}
                              />
                            </div>

                            <div className="flex space-x-2 justify-center">
                              {doc.type !== 'evCer' ? (
                                <>
                                  {/* View Button */}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => {
                                      if (merchant.mobileNo && token) {
                                        fetchMerchantDocument(merchant.mobileNo, doc.type, token, true)
                                          .then((url: string | null) => {
                                            const finalUrl = url || getImageUrl(merchant, doc.type);
                                            handlePreviewImage(finalUrl);
                                          })
                                          .catch(() => {
                                            const fallbackUrl = getImageUrl(merchant, doc.type);
                                            handlePreviewImage(fallbackUrl);
                                          });
                                      } else {
                                        const fallbackUrl = getImageUrl(merchant, doc.type);
                                        handlePreviewImage(fallbackUrl);
                                      }
                                    }}
                                  >
                                    <Eye size={14} className="mr-1" />
                                    View
                                  </Button>

                                  {/* Download Button */}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => {
                                      if (merchant.mobileNo && token) {
                                        fetchMerchantDocument(merchant.mobileNo, doc.type, token, true)
                                          .then((url: string | null) => {
                                            handleDownloadImage(url || getImageUrl(merchant, doc.type), doc.label);
                                          })
                                          .catch(() => {
                                            const fallbackUrl = getImageUrl(merchant, doc.type);
                                            handleDownloadImage(fallbackUrl, doc.label);
                                          });
                                      } else {
                                        const fallbackUrl = getImageUrl(merchant, doc.type);
                                        handleDownloadImage(fallbackUrl, doc.label);
                                      }
                                    }}
                                  >
                                    <Download size={14} className="mr-1" />
                                    Download
                                  </Button>
                                </>
                              ) : (
                                // Only Download button centered for evCer
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full justify-center"
                                  onClick={() => {
                                    if (merchant.mobileNo && token) {
                                      fetchMerchantDocument(merchant.mobileNo, doc.type, token, true)
                                        .then((url: string | null) => {
                                          handleDownloadImage(url || getImageUrl(merchant, doc.type), doc.label);
                                        })
                                        .catch(() => {
                                          const fallbackUrl = getImageUrl(merchant, doc.type);
                                          handleDownloadImage(fallbackUrl, doc.label);
                                        });
                                    } else {
                                      const fallbackUrl = getImageUrl(merchant, doc.type);
                                      handleDownloadImage(fallbackUrl, doc.label);
                                    }
                                  }}
                                >
                                  <Download size={14} className="mr-1" />
                                  Download
                                </Button>
                              )}
                            </div>

                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-32 text-gray-400 bg-gray-100 rounded">
                            <div className="text-center">
                              {doc.type === 'evCer' ? (
                                <>
                                  <FileTextIcon size={24} className="mx-auto mb-1" />
                                  <span className="text-xs">No PDF Available</span>
                                </>
                              ) : (
                                <>
                                  <ImageIcon size={24} className="mx-auto mb-1" />
                                  <span className="text-xs">No Image Available</span>
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {isEditing && canManage && (
                          <div className="mt-2">
                            <input
                              src={docPreviews[doc.type] || getImageUrl(merchant, doc.type)}
                              type="file"
                              ref={el => { inputRefs.current[doc.type] = el; }}
                              accept={doc.type === 'evCer' ? 'application/pdf' : 'image/*'}
                              style={{ display: 'none' }}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  // 1. Show instant preview using local file
                                  const localPreviewUrl = URL.createObjectURL(file);
                                  setDocPreviews((prev) => ({
                                    ...prev,
                                    [doc.type]: localPreviewUrl,
                                  }));

                                  // 2. Upload the document
                                  const result = await handleDocumentUpload(file, "merchant", merchant.mobileNo, doc.type);

                                  if (result && result.status === 200 && token) {
                                    setDocumentUploaded(true);

                                    // Keep showing local preview for now
                                    const localPreviewUrl = URL.createObjectURL(file);
                                    setDocPreviews((prev) => ({
                                      ...prev,
                                      [doc.type]: localPreviewUrl,
                                    }));

                                    // Fetch new URL
                                    let newUrl = await fetchMerchantDocument(merchant.mobileNo, doc.type, token, true);
                                    console.log("New URL fetched:", newUrl);

                                    if (newUrl) {
                                      newUrl = `${newUrl}${newUrl.includes('?') ? '&' : '?'}cb=${Date.now()}`;

                                      // Test loadability of the new image
                                      const img = new Image();
                                      img.onload = () => {
                                        // Only switch preview if it loads successfully
                                        setDocPreviews((prev) => ({
                                          ...prev,
                                          [doc.type]: newUrl,
                                        }));
                                      };
                                      img.onerror = () => {
                                        console.warn("Failed to load image from signed URL, keeping local preview.");
                                      };
                                      img.src = newUrl;
                                    }

                                    onUpdate();
                                  }


                                  // 4. Clear input
                                  e.target.value = "";
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              variant="primary"
                              className="w-full"
                              onClick={() => inputRefs.current[doc.type]?.click()}
                            >
                              {hasDocument(merchant, doc.type) ? 'Change Document' : 'Upload Document'}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </TabsContent>

            {/* Bank Details Tab */}
            <TabsContent value="bank" className="space-y-6">
              <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                <h3 className="mb-3 text-sm font-medium text-gray-700">Bank Account Information</h3>
                {isEditing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Account Holder Name
                      </label>
                      <input
                        type="text"
                        name="holderName"
                        value={formData.kycBank?.holderName}
                        onChange={handleBankInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Account Number
                      </label>
                      <input
                        type="text"
                        name="accountNo"
                        value={formData.kycBank?.accountNo}
                        onChange={handleBankInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        IFSC Code
                      </label>
                      <input
                        type="text"
                        name="ifsc"
                        value={formData.kycBank?.ifsc}
                        onChange={handleBankInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <User size={16} className="text-gray-500 mt-0.5 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-800">{merchant.kycBank.holderName}</div>
                        <div className="text-xs text-gray-500">Account Holder Name</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <CreditCard size={16} className="text-gray-500 mt-0.5 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-800">{merchant.kycBank.accountNo}</div>
                        <div className="text-xs text-gray-500">Account Number</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Building2 size={16} className="text-gray-500 mt-0.5 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-800">{merchant.kycBank.ifsc}</div>
                        <div className="text-xs text-gray-500">IFSC Code</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Logs Tab */}
            <TabsContent value="logs" className="space-y-6">
              <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                <h3 className="mb-3 text-sm font-medium text-gray-700">Logs</h3>
                {merchant.logs && merchant.logs.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {merchant.logs.slice().reverse().map((log, index) => (
                      <div key={index} className="p-3 bg-white border border-gray-200 rounded-md">
                        <div className="flex items-start">
                          <ClipboardCheck size={16} className="text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                          <div className="text-sm text-gray-700">{log}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8">
                    <FileText size={40} className="mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">No Logs available</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Document Preview"
        size="lg"
      >
        {previewImage && (
          <div className="flex items-center justify-center">
            <img
              src={previewImage}
              alt="Document Preview"
              className="max-w-full max-h-[calc(80vh-100px)] object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = getImageUrl(null, 'shopFront');
              }}
            />
          </div>
        )}
        <div className="flex justify-end mt-4">
          {previewImage && (
            <Button
              variant="outline"
              onClick={() => handleDownloadImage(previewImage, 'Document')}
              className="mr-2"
            >
              <Download size={14} className="mr-1" />
              Download
            </Button>
          )}
          <Button onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default MerchantDetailModal;